<template>
  <v-container fluid>
    <v-row class="align-center">
      <v-col>
        <h1 class="text-h4"><v-icon class="mr-2">mdi-star</v-icon>Reviews</h1>
      </v-col>
      <v-col cols="auto" class="d-flex ga-2">
        <v-btn icon color="primary" variant="flat" @click="loadReviews" :loading="loading">
          <v-icon size="30">mdi-refresh</v-icon>
          <v-tooltip activator="parent" location="bottom">Load Reviews</v-tooltip>
        </v-btn>
        <v-menu v-if="isAdmin">
          <template v-slot:activator="{ props }">
            <v-btn icon variant="flat" v-bind="props">
              <v-icon size="30">mdi-dots-vertical</v-icon>
              <v-tooltip activator="parent" location="bottom">More</v-tooltip>
            </v-btn>
          </template>
          <v-list density="compact" nav>
            <v-list-item
              prepend-icon="mdi-chart-box-outline"
              title="Coverage Report"
              @click="openReviewsCoverageDialog"
            />
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <!-- Progress Info -->
        <v-alert
          v-if="reviewsData && reviewsData.totalAssigned > 0"
          :type="reviewsData.remainingToday === 0 ? 'success' : 'info'"
          variant="tonal"
          class="mb-4"
          density="compact"
        >
          <span v-if="reviewsData.remainingToday === 0">
            All {{ reviewsData.totalAssigned }} combinations copied today
          </span>
          <span v-else>
            {{ reviewsData.copiedToday }} of {{ reviewsData.totalAssigned }} copied today
            &mdash; {{ reviewsData.remainingToday }} remaining
          </span>
        </v-alert>

        <!-- Reviews Display -->
        <div v-if="reviews.length > 0">
          <v-card
            v-for="(review, index) in reviews"
            :key="index"
            :class="{ 'review-shown': copiedReviewIds.has(review.id) }"
            class="mb-3 pa-3"
          >
            <div class="d-flex justify-space-between align-start">
              <div class="flex-grow-1 mr-3">
                <div class="text-body-1 mb-2">{{ review.text }}</div>
                <div class="d-flex align-center flex-wrap ga-2">
                  <span class="d-flex align-center">
                    {{ review.rating }}
                    <v-icon icon="mdi-star" color="orange" size="small" class="ml-1"></v-icon>
                  </span>
                  <v-chip size="x-small" color="blue" variant="outlined">
                    {{ review.platform }}
                  </v-chip>
                  <v-chip size="x-small" color="green" variant="outlined">
                    {{ review.location }}
                  </v-chip>
                  <v-chip size="x-small" color="purple" variant="outlined">
                    {{ review.brand }}
                  </v-chip>
                </div>
              </div>
              <div class="d-flex ga-1">
                <v-tooltip text="Edit review" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="tonal"
                      color="secondary"
                      @click="openEditDialog(review)"
                      v-if="isAdmin"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip text="Copy to clipboard" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="tonal"
                      color="primary"
                      @click="copyToClipboard(review, index)"
                    >
                      <v-icon v-if="copiedIndex === index" color="success">mdi-check</v-icon>
                      <v-icon v-else>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>
            </div>
          </v-card>
        </div>

        <v-empty-state
          v-else-if="!loading && !hasLoaded"
          class="text-center py-8"
          headline="No reviews loaded yet"
          text=""
          icon="mdi-message-text-outline"
          action-text="Load Reviews"
          @click:action="loadReviews"
        >
        </v-empty-state>

        <v-empty-state
          v-else-if="!loading && hasLoaded && reviewsData?.totalAssigned === 0"
          class="text-center py-8"
          headline="No review combinations assigned to you"
          text="Ask an admin to assign you combinations in Settings > Review Rules"
          icon="mdi-clipboard-alert-outline"
        >
        </v-empty-state>

        <v-empty-state
          v-else-if="!loading && hasLoaded && reviewsData?.remainingToday === 0"
          class="text-center py-8"
          :headline="`All ${reviewsData?.totalAssigned || 0} combinations copied today`"
          text="Come back tomorrow for fresh reviews"
          icon="mdi-check-circle-outline"
        >
        </v-empty-state>

        <v-empty-state
          v-else-if="!loading && hasLoaded"
          class="text-center py-8"
          headline="No eligible reviews"
          text="All reviews for your assigned combinations are in cooldown"
          icon="mdi-message-text-outline"
        >
        </v-empty-state>
      </v-col>
    </v-row>

    <!-- Edit Dialog -->
    <BaseDialog
      v-model="editDialog"
      title="Edit Review"
      max-width="600px"
      persistent
      cancel-text="Cancel"
      save-text="Save"
      :save-loading="saving"
      @save="saveReview"
    >
          <v-form @submit.prevent="saveReview">
            <v-rating v-model="editForm.rating" color="orange" hover class="mb-4"></v-rating>

            <v-textarea
              v-model="editForm.text"
              label="Review Text"
              rows="4"
              auto-grow
              density="comfortable"
              variant="outlined"
              required
            ></v-textarea>

            <v-row class="mt-2">
              <v-col cols="4">
                <v-select
                  v-model="editForm.platform"
                  :items="editFormOptions.platforms"
                  item-title="name"
                  item-value="name"
                  label="Platform"
                  density="comfortable"
                  variant="outlined"
                  hide-details
                >
                  <template v-slot:item="{ item, props }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <div :style="{ background: getPlatformGradient(item.name), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
                      </template>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item }">
                    <div class="d-flex align-center">
                      <div :style="{ background: getPlatformGradient(item.name), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
                      {{ item.name }}
                    </div>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="editForm.location"
                  :items="editFormOptions.locations"
                  item-title="name"
                  item-value="name"
                  label="Location"
                  density="comfortable"
                  variant="outlined"
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="editForm.brand"
                  :items="editFormOptions.brands"
                  item-title="name"
                  item-value="name"
                  label="Brand"
                  density="comfortable"
                  variant="outlined"
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
    </BaseDialog>

    <!-- Reviews Coverage Report Dialog -->
    <ReviewsCoverageReportDialog />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref } from "vue";
import api from "@/utils/api.js";

import { useApp } from "@/composables/useApp";
import { usePlatformColors } from "@/composables/usePlatformColors.js";
const { isAdmin } = useApp();
const { getPlatformGradient } = usePlatformColors();

import BaseDialog from "@/components/BaseDialog.vue";
import ReviewsCoverageReportDialog from "@/components/ReviewsCoverageReportDialog.vue";

import { useReviews } from "@/composables/useReviews.js";
const {
  platforms,
  locations,
  brands,
  loadPlatformsAndLocations,
  openReviewsCoverageDialog,
} = useReviews();

const reviews = ref([]);
const reviewsData = ref(null);
const loading = ref(false);
const hasLoaded = ref(false);

const snackbar = ref({
  show: false,
  text: "",
  timeout: 3000,
  color: "",
});

const showSnack = (message, color = "success") => {
  snackbar.value = { show: true, message, color };
};

const copiedIndex = ref(null);
const copiedReviewIds = ref(new Set());

// Edit dialog state
const editDialog = ref(false);
const saving = ref(false);
const editForm = ref({
  id: null,
  rating: 5,
  text: "",
  platform: "Talabat",
  location: "Arjan",
  brand: "Felice",
});

// Edit form options loaded on-demand
const editFormOptions = ref({ platforms: [], locations: [], brands: [] });

const openEditDialog = async (review) => {
  // Load options on-demand if not yet loaded
  if (editFormOptions.value.platforms.length === 0) {
    await loadPlatformsAndLocations();
    editFormOptions.value = {
      platforms: platforms.value,
      locations: locations.value,
      brands: brands.value,
    };
  }

  editForm.value = {
    id: review.id,
    rating: review.rating,
    text: review.text,
    platform: review.platform,
    location: review.location,
    brand: review.brand || "Felice",
  };
  editDialog.value = true;
};

const saveReview = async () => {
  if (!editForm.value.text.trim()) {
    showSnack("Review text is required", "error");
    return;
  }

  saving.value = true;
  try {
    await api.marketingApi.put(`/api/marketing/reviews/admin/reviews/${editForm.value.id}`, {
      rating: editForm.value.rating,
      text: editForm.value.text,
      platform: editForm.value.platform,
      location: editForm.value.location,
      brand: editForm.value.brand,
    });

    // Update the review in the local list
    const index = reviews.value.findIndex((r) => r.id === editForm.value.id);
    if (index !== -1) {
      reviews.value[index] = {
        ...reviews.value[index],
        rating: editForm.value.rating,
        text: editForm.value.text,
        platform: editForm.value.platform,
        location: editForm.value.location,
        brand: editForm.value.brand,
      };
    }

    showSnack("Review updated successfully", "success");
    editDialog.value = false;
  } catch (err) {
    console.error("Failed to update review:", err);
    showSnack(err.response?.data?.error || "Failed to update review", "error");
  } finally {
    saving.value = false;
  }
};

const copyToClipboard = async (review, index) => {
  try {
    await navigator.clipboard.writeText(review.text);

    copiedIndex.value = index;
    copiedReviewIds.value = new Set([...copiedReviewIds.value, review.id]);

    // Mark review as shown on the backend
    try {
      await api.marketingApi.post(`/api/marketing/reviews/${review.id}/mark-shown`);
    } catch (markErr) {
      console.error("Failed to mark review as shown:", markErr);
    }

    setTimeout(() => {
      copiedIndex.value = null;
    }, 2000);

    showSnack("Review copied to clipboard!", "success");
  } catch (err) {
    console.error("Failed to copy text: ", err);

    // Fallback for older browsers
    try {
      const textArea = document.createElement("textarea");
      textArea.value = review.text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      copiedIndex.value = index;
      copiedReviewIds.value = new Set([...copiedReviewIds.value, review.id]);

      try {
        await api.marketingApi.post(`/api/marketing/reviews/${review.id}/mark-shown`);
      } catch (markErr) {
        console.error("Failed to mark review as shown:", markErr);
      }

      setTimeout(() => {
        copiedIndex.value = null;
      }, 2000);

      showSnack("Review copied to clipboard!", "success");
    } catch (fallbackErr) {
      console.error("Fallback copy failed: ", fallbackErr);
      showSnack("Failed to copy to clipboard", "error");
    }
  }
};

const loadReviews = async () => {
  loading.value = true;
  reviews.value = [];
  copiedReviewIds.value = new Set();

  try {
    const res = await api.marketingApi.get("/api/marketing/reviews/random-set");
    reviewsData.value = res.data;
    reviews.value = res.data.reviews || [];
    hasLoaded.value = true;

    if (reviews.value.length > 0) {
      showSnack(`Loaded ${reviews.value.length} reviews`, "success");
    }
  } catch (err) {
    console.error("Failed to load reviews:", err);
    showSnack("Failed to load reviews", "error");
  } finally {
    loading.value = false;
  }
};

</script>

<style>
.review-shown {
  opacity: 0.5;
}
</style>
